import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { MimeTypeEnum } from '@/graphql/types';
import React, { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useFilterBarCtx } from '../FilterBar';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const mimeTypeSearchSchema = z.object({
  mimeTypes: z.array(z.nativeEnum(MimeTypeEnum)).nullish(),
});

const mimeTypeComponentProps = z.object({
  displayName: z.string().optional(),
});

type MimeTypeFilterProps = z.infer<typeof mimeTypeComponentProps>;

const getOptions = () => {
  const enums = Object.values(MimeTypeEnum);

  const options = new Map<string, MimeTypeEnum[]>();
  for (const enumValue of enums) {
    const tokens = enumValue.split('_');
    let type = tokens[0];

    if (enumValue === MimeTypeEnum.ApplicationPdf) {
      type = 'PDF';
    }
    if (enumValue === MimeTypeEnum.ImageGif) {
      type = 'GIF';
    }

    if (enumValue === MimeTypeEnum.TextCsv) {
      type = 'CSV';
    }

    if (options.has(type)) {
      options.get(type)!.push(enumValue);
    } else {
      options.set(type, [enumValue]);
    }
  }
  return options;
};

const getValueFromOptionKey = (key: string) => {
  const options = getOptions();
  return options.get(key) || [];
};

const options = getOptions();

const MimeTypeFilter = ({ displayName = 'Media Types' }: MimeTypeFilterProps) => {
  const { props: ctxProps } = useFilterBarCtx();

  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(mimeTypeSearchSchema);

  useEffect(() => {
    const selectedTemplates = slowFilters?.mimeTypes;
    if (!selectedTemplates) return;

    const allowedTemplates = Object.values(MimeTypeEnum).map((option) => option);

    const validTemplates = selectedTemplates.filter((template) => allowedTemplates.includes(template));

    if (validTemplates.length !== selectedTemplates.length) {
      updateFilter({
        mimeTypes: validTemplates.length ? validTemplates : undefined,
      });
    }
  }, [slowFilters.mimeTypes, updateFilter]);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    const menu: DropDownMenuItemProps[] = [];
    options.forEach((value, key) => {
      menu.push({
        name: key,
        value: value,
        onClick: () => {
          const selectedOptions = getValueFromOptionKey(key);
          updateFilter((prev) => {
            let newTemplates = [...(prev?.mimeTypes || []), ...selectedOptions];
            if (prev?.mimeTypes?.some((template) => selectedOptions.includes(template))) {
              newTemplates = prev.mimeTypes.filter((template) => !selectedOptions.includes(template));
            }

            return {
              mimeTypes: newTemplates.length ? newTemplates : undefined,
            };
          });
        },
      });
    });

    return menu;
  }, [updateFilter]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className={ctxProps?.variant === 'default' ? 'z-btn-6' : 'z-btn-compact'}
        name={displayName}
        menuList={menuItems}
        type="multiple-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'mimeTypes',
          isSearchable: true,
          filterBarComparison(itemState, filterParam) {
            if (
              !!filterParam &&
              !!itemState &&
              (itemState as MimeTypeEnum[])?.every((item) => (filterParam as MimeTypeEnum[])?.includes(item))
            ) {
              return true;
            }

            return false;
          },
        }}
      />
      <FilterBadge count={fastFilters?.mimeTypes?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const MimeTypeMemo = React.memo(MimeTypeFilter);

export const FilterConfig_MimeTypes = {
  mimeTypes: {
    searchSchema: mimeTypeSearchSchema,
    component: MimeTypeMemo,
    componentProps: mimeTypeComponentProps,
  },
} as const;
